import JSZip from 'jszip';
import { calculateSequence, getPageDimensions } from './pdfGenerator';

const EXPORT_WIDTH = 1600;

const getGridConfig = (count) => {
  if (count <= 1) return { rows: 1, cols: 1 };
  if (count === 2) return { rows: 2, cols: 1 };
  if (count <= 4) return { rows: 2, cols: 2 };
  if (count <= 6) return { rows: 3, cols: 2 };
  if (count <= 8) return { rows: 4, cols: 2 };
  return { rows: 4, cols: 3 };
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const drawImage = (ctx, image, x, y, width, height, mode) => {
  if (mode === 'fill') {
    const sourceRatio = image.width / image.height;
    const targetRatio = width / height;
    let sourceWidth = image.width;
    let sourceHeight = image.height;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceRatio > targetRatio) {
      sourceWidth = image.height * targetRatio;
      sourceX = (image.width - sourceWidth) / 2;
    } else {
      sourceHeight = image.width / targetRatio;
      sourceY = (image.height - sourceHeight) / 2;
    }

    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      x,
      y,
      width,
      height
    );
    return;
  }

  const ratio = Math.min(width / image.width, height / image.height);
  const imageWidth = image.width * ratio;
  const imageHeight = image.height * ratio;

  ctx.drawImage(
    image,
    x + (width - imageWidth) / 2,
    y + (height - imageHeight) / 2,
    imageWidth,
    imageHeight
  );
};

const renderPage = async (items, settings) => {
  const dimensions = getPageDimensions(
    settings,
    items[0]?.aspectRatio || 1
  );
  const scale = EXPORT_WIDTH / dimensions.w;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(dimensions.w * scale);
  canvas.height = Math.round(dimensions.h * scale);

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const grid = getGridConfig(items.length);
  const margin = Number(settings.margin) || 0;
  const cellWidth = (dimensions.w - margin * 2) / grid.cols;
  const cellHeight = (dimensions.h - margin * 2) / grid.rows;
  const cellPadding = items.length > 1 ? 2 : 0;
  const textHeight = settings.showDetails ? 8 : 0;
  const loadedImages = await Promise.all(
    items.map((item) =>
      item.type === 'image' ? loadImage(item.dataUrl) : Promise.resolve(null)
    )
  );

  items.forEach((item, index) => {
    if (item.type === 'blank') return;

    const row = Math.floor(index / grid.cols);
    const column = index % grid.cols;
    const startX = (margin + column * cellWidth + cellPadding) * scale;
    const startY = (margin + row * cellHeight + cellPadding) * scale;
    const targetWidth = (cellWidth - cellPadding * 2) * scale;
    const targetHeight = Math.max(1, cellHeight - cellPadding * 2 - textHeight) * scale;

    ctx.fillStyle = settings.imageBackground || '#ffffff';
    ctx.fillRect(startX, startY, targetWidth, targetHeight);
    drawImage(
      ctx,
      loadedImages[index],
      startX,
      startY,
      targetWidth,
      targetHeight,
      settings.fitMode
    );

    if (settings.showDetails) {
      ctx.fillStyle = '#646464';
      ctx.font = `${Math.max(12, Math.round(8 * scale))}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(
        `${item.name} (${Math.round(item.width)}x${Math.round(item.height)}px)`,
        startX + targetWidth / 2,
        startY + targetHeight + 5 * scale
      );
    }
  });

  if (settings.showPageNumbers) {
    ctx.fillStyle = '#969696';
    ctx.font = `${Math.max(14, Math.round(10 * scale))}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(
      `Page ${items.pageNumber}`,
      canvas.width / 2,
      canvas.height - 5 * scale
    );
  }

  return canvas;
};

export const exportPagesAsZip = async (
  images,
  settings,
  format,
  onProgress
) => {
  const pages = calculateSequence(images, settings);
  const zip = new JSZip();
  const extension = format === 'png' ? 'png' : 'jpg';
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';

  for (let index = 0; index < pages.length; index += 1) {
    const pageItems = pages[index].map((item) => ({
      ...item,
      pageNumber: index + 1,
    }));
    const canvas = await renderPage(pageItems, settings);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, format === 'png' ? undefined : 0.92)
    );

    zip.file(`page-${String(index + 1).padStart(3, '0')}.${extension}`, blob);
    onProgress(Math.round(((index + 1) / pages.length) * 100));
  }

  const archive = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(archive);
  const link = document.createElement('a');
  link.href = url;
  link.download = `picfolio-pages-${extension}.zip`;
  link.click();
  URL.revokeObjectURL(url);
  onProgress(0);
};